/**
 * Recording an enquiry.
 *
 * The first screen anyone touches on a working day, and the one that has to be
 * fastest: somebody is usually on the telephone while it is filled in. So only
 * a name is required. Everything else can be added once the call has ended,
 * and a half-recorded enquiry is worth far more than a lost one.
 *
 * The server may answer 201 with `duplicate_candidates`. That is not an error
 * and the lead is created either way — it says somebody with a similar name or
 * address already exists, and a person should look. Silently refusing would
 * lose the enquiry; silently creating would let the firm work the same client
 * twice from two files.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CustomFieldValues,
  type Lead,
  type LeadSource,
} from '@legalcrm/api';
import { Card, CardBody, Form, FormActions, FormInput, FormSelect, FormTextarea, useZodForm } from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { CustomFieldInputs, problemsFrom } from '@/components/customFields/CustomFields';
import { PageHeader } from '@/shell/PageHeader';

const schema = z.object({
  full_name: z.string().min(1, 'Enter the enquirer’s name'),
  lead_type: z.enum(['individual', 'organization']),
  email: z.string().email('Enter a valid email address').or(z.literal('')),
  phone: z.string(),
  source_key: z.string(),
  enquiry_text: z.string(),
  location: z.string(),
});

type Values = z.infer<typeof schema>;

export function NewLeadPage() {
  const navigate = useNavigate();
  const api = useApi();

  /**
   * Held outside the zod form deliberately. These fields are defined by the firm at
   * runtime, so there is no schema to put them in — the server validates them against
   * the same definitions the CSV importer uses, and hands back problems keyed by field.
   */
  const [custom, setCustom] = useState<CustomFieldValues>({});

  const sources = useApiQuery<{ results: LeadSource[] } | LeadSource[]>(
    qk.list('lead-sources'),
    STAFF.leadSources,
  );
  const sourceRows = Array.isArray(sources.data)
    ? sources.data
    : (sources.data?.results ?? []);

  const form = useZodForm(schema, {
    defaultValues: {
      full_name: '',
      lead_type: 'individual',
      email: '',
      phone: '',
      source_key: 'manual',
      enquiry_text: '',
      location: '',
    },
  });

  const create = useApiMutation<Lead, Values>({
    mutationFn: (values) =>
      api.post<Lead>(`${STAFF.leads}/`, {
        ...values,
        // The API takes an absent value, not an empty string, for anything
        // optional. Sending "" would record that the client has no email
        // address rather than that nobody asked yet.
        email: values.email || undefined,
        phone: values.phone || undefined,
        enquiry_text: values.enquiry_text || undefined,
        location: values.location || undefined,
        custom_fields: custom,
      }),
    invalidates: [qk.all('leads')],
  });

  return (
    <>
      <PageHeader
        title="New enquiry"
        description="Only a name is needed. The rest can follow once the call has ended."
      />

      <Card className="max-w-2xl">
        <CardBody>
          <Form
            form={form}
            ariaLabel="New enquiry"
            error={create.error}
            onSubmit={async (values) => {
              const lead = await create.mutateAsync(values);
              navigate(`/leads/${lead.id}`);
            }}
          >
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <FormInput
                form={form}
                name="full_name"
                label="Name"
                autoFocus
                autoComplete="off"
              />
              {/*
                Asked here, not left to be inferred from the name later. It decides
                whether the enquirer's own name and email are the right thing to
                pre-fill when a signatory is added to their engagement letter — an
                organisation's signatory is a person at the company, never the
                company itself, so guessing wrong there would offer to sign someone
                else's name onto the letter.
              */}
              <FormSelect
                form={form}
                name="lead_type"
                label="Individual or organization"
                options={[
                  { value: 'individual', label: 'Individual' },
                  { value: 'organization', label: 'Organization' },
                ]}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput form={form} name="email" label="Email" type="email" />
              <FormInput form={form} name="phone" label="Telephone" type="tel" />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormSelect
                form={form}
                name="source_key"
                label="How they found the firm"
                options={sourceRows.map((source) => ({
                  value: source.key,
                  label: source.name,
                }))}
              />
              <FormInput form={form} name="location" label="Location" />
            </div>

            <FormTextarea
              form={form}
              name="enquiry_text"
              label="What they asked about"
              rows={4}
              hint="In their words where possible. It is what the lawyer reads first."
            />

            <CustomFieldInputs
              entity="lead"
              values={custom}
              onChange={setCustom}
              problems={problemsFrom(create.error)}
            />

            <FormActions
              submitLabel="Record enquiry"
              busy={create.isPending}
              onCancel={() => navigate('/leads')}
            />
          </Form>
        </CardBody>
      </Card>
    </>
  );
}
